/**
 * GameHealthChipCard — iter84.63 Phase B · Release Ops de-duplication.
 *
 * Replaces the mirrored Drift Watch / Orphan Picks / Scoring Drift cards on
 * the Release Ops runbook. Deploy verification only needs the VERDICT —
 * the full detector/healer cards live at their one canonical home
 * (Game Ops). Sourced from GET /api/game-health/summary.
 */
import { useEffect, useState } from "react";
import { HeartPulse, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { CopyDiagButton, buildDiagText, diagLine } from "@/components/site-admin/release-ops/CopyDiagButton";
import { WhatsThisButton } from "./WhatsThisButton";

const POSTURE_PILL = {
    blocked: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    warn:    "bg-amber-500/15 text-amber-300 border-amber-500/40",
    notice:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
    ok:      "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

export function GameHealthChipCard({ onOpenGameOps }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.gameHealthSummary().then(setData)
            .catch((e) => setError(e?.response?.data?.detail || "unavailable"));
    }, []);

    const posture = data?.overall || "ok";

    return (
        <div
            className="brutal-card p-3 border border-emerald-900/40 bg-emerald-950/10 flex items-center gap-3 flex-wrap"
            data-testid="game-health-chip-card"
        >
            <HeartPulse className="w-4 h-4 text-emerald-300 shrink-0" />
            <span className="font-display font-extrabold text-xs uppercase tracking-wide text-zinc-200">
                Game health
            </span>
            {error ? (
                <span className="text-[10px] text-zinc-500" data-testid="game-health-chip-error">{error}</span>
            ) : !data ? (
                <span className="text-[10px] text-zinc-500 italic">loading…</span>
            ) : (
                <>
                    <span
                        className={`pill text-[9px] font-bold uppercase border ${POSTURE_PILL[posture] || POSTURE_PILL.warn}`}
                        data-testid="game-health-chip-posture"
                    >
                        {posture}
                    </span>
                    <span className="text-[10px] text-zinc-400" data-testid="game-health-chip-detail">
                        {data.issue_count === 0
                            ? "All game-data detectors clean."
                            : `${data.issue_count} open finding(s): ${data.issues.map((i) => i.label).slice(0, 3).join(" · ")}${data.issue_count > 3 ? "…" : ""}`}
                    </span>
                </>
            )}
            <span className="ml-auto flex items-center gap-1">
                <WhatsThisButton
                    testId="game-health-chip-whats-this"
                    title="Game health (posture chip)"
                    what="A live snapshot of the Game Health Registry — the same detectors that power Product Ops — shown here so you see game posture at deploy time without leaving Release Ops."
                    lookFor="OK. If it shows findings, click through to Product Ops where each finding has its paired one-click healer."
                    notFor="Fixing anything — this chip is read-only; healing happens in Product Ops."
                />
                {data && (
                    <CopyDiagButton
                        testId="game-health-chip-copy-diag-btn"
                        getText={() => buildDiagText({
                            card: "Game health (registry rollup)",
                            verdict: data.overall,
                            summary: `${data.issue_count} open finding(s)`,
                            lines: (data.issues || []).map((i) =>
                                diagLine(i.posture, `${i.label}${i.count ? ` ×${i.count}` : ""}`, i.detail)),
                        })}
                    />
                )}
                <button
                    type="button"
                    onClick={onOpenGameOps}
                    data-testid="game-health-chip-open-btn"
                    className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 border border-emerald-800 text-emerald-300 rounded hover:bg-emerald-900/30"
                >
                    View in Product Ops <ArrowRight className="w-3 h-3" />
                </button>
            </span>
        </div>
    );
}
